/* eslint-disable max-statements */
/* eslint-disable complexity */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useContext, useCallback } from 'react';
import { Grid } from '@elliemae/ds-grid';
import { SingleMenuItem, MultiMenuItem, Separator } from '@elliemae/ds-menu-items';
import { styled } from '@elliemae/ds-system';
import { useOwnerProps } from '@elliemae/ds-props-helpers';
import { Section } from '../Section.js';
import { ComboBoxContext } from '../../ComboBoxCTX.js';
import { StyledCreatableLabel, StyledCreatableValue } from './styled.js';
import { isSelected, getSuggestedValueOnChange } from '../../utils/listHelper.js';
import { ComboboxDataTestid } from '../../ComboboxDataTestids.js';
import type { DSComboboxT } from '../../react-desc-prop-types.js';
import { MENU_OPTION_TYPES, INTERNAL_MENU_OPTION_TYPES } from '../../constants.js';
import { DSFormComboboxName, FORM_COMBOBOX_SLOTS } from '../../constants/index.js';

interface ItemRendererT {
  extraItemProps?: {
    itemList?: DSComboboxT.OptionTypes[];
  };
  index: number;
}

const StyledSingleMenuItem = styled(SingleMenuItem, {
  name: DSFormComboboxName,
  slot: FORM_COMBOBOX_SLOTS.SINGLE_MENU_ITEM,
  preserveLegacyDataTestId: true,
})``;

const StyledMultiMenuItem = styled(MultiMenuItem, {
  name: DSFormComboboxName,
  slot: FORM_COMBOBOX_SLOTS.MULTI_MENU_ITEM,
  preserveLegacyDataTestId: true,
})``;

export const ItemRenderer = ({ index, extraItemProps }: ItemRendererT): JSX.Element => {
  const {
    props,
    props: { onCreate, onChange, isNonClearable, selectedValues, onFilter, allOptions, readOnly },
    setMenuState,
    inputValue,
    focusOptionIdx,
    setInputValue,
    optionsPerSection,
  } = useContext(ComboBoxContext);

  const { getOwnerProps } = useOwnerProps(props);
  const getOwnerPropsArguments = useCallback(
    () => ({
      index,
    }),
    [index],
  );

  const option = extraItemProps?.itemList?.[index];

  const multiple = Array.isArray(selectedValues);
  const CBItem = multiple ? StyledMultiMenuItem : StyledSingleMenuItem;

  const handleOnCreateClick = useCallback(() => {
    if (inputValue && onCreate) {
      onCreate(inputValue);
      setInputValue('');
      if (onFilter) onFilter(allOptions, inputValue);
    }
  }, [onFilter, onCreate, inputValue, allOptions, setInputValue]);

  const handleClick = useCallback(
    (currentOption: DSComboboxT.ItemOption, e: React.MouseEvent) => {
      if (readOnly) return;
      if (currentOption.type === MENU_OPTION_TYPES.OPTION) {
        if (!currentOption.disabled) {
          if (onFilter) onFilter(allOptions, inputValue);
          setInputValue('');
          if (!multiple) {
            setMenuState(false, 'selectOption', e);
          }
          onChange(getSuggestedValueOnChange(currentOption, selectedValues, isNonClearable), currentOption, e);
        }
      }
      // prevent for loosing focus on input control
      e.stopPropagation();
      e.preventDefault();
    },
    [onFilter, selectedValues, allOptions, onChange, setMenuState, multiple],
  );

  // prevent blur from controls input
  const handleOnMouseDown = useCallback((e: React.MouseEvent<HTMLLIElement>) => {
    e.preventDefault();
  }, []);

  // @ts-expect-error - this needs to be rewritten, this should not be a custom hook returning JSX, this should be a component.
  return useMemo(() => {
    if (option) {
      const { dsId, type, disabled, applyAriaDisabled, readOnly: itemReadOnly, ...rest } = option;
      const generalProps = {
        // 'aria-setsize': `${allOptions.length}`,
        // 'aria-posinset': `${vItem.index + 1}`,
        key: `${dsId}`,
        dsId: dsId.toString(),
        ...rest,
      };
      if (type === MENU_OPTION_TYPES.SECTION) {
        return (
          <Section
            label={option.label}
            {...generalProps}
            // @ts-expect-error - this needs to be rewritten, broken typescript typings?
            options={option.options}
            handleClick={handleClick}
            focusOptionIdx={focusOptionIdx}
            handleOnMouseDown={handleOnMouseDown}
            selectedValues={selectedValues}
            getOwnerProps={getOwnerProps}
            getOwnerPropsArguments={getOwnerPropsArguments}
          />
        );
      }
      if (type === MENU_OPTION_TYPES.SEPARATOR) {
        return <Separator {...generalProps} render={undefined} />;
      }
      if (option.type === MENU_OPTION_TYPES.OPTION) {
        return (
          // @ts-expect-error - this needs to be rewritten, this should not be a custom hook returning JSX, this should be a component.
          <CBItem
            {...generalProps}
            value={option.value}
            label={option.label}
            dataTestid={ComboboxDataTestid.OPTION}
            disabled={disabled}
            applyAriaDisabled={applyAriaDisabled || itemReadOnly}
            onClick={(e: React.MouseEvent) => {
              if (applyAriaDisabled) return;
              handleClick(option, e);
            }}
            onMouseDown={handleOnMouseDown}
            isActive={dsId === focusOptionIdx}
            tabIndex={-1}
            isSelected={isSelected(selectedValues, option)}
            getOwnerProps={getOwnerProps}
            getOwnerPropsArguments={getOwnerPropsArguments}
          />
        );
      }
      if (type === INTERNAL_MENU_OPTION_TYPES.CREATABLE) {
        return (
          <StyledSingleMenuItem
            getOwnerProps={getOwnerProps}
            getOwnerPropsArguments={getOwnerPropsArguments}
            dataTestid={ComboboxDataTestid.OPTION}
            isActive={dsId === focusOptionIdx}
            {...generalProps}
            render={({ label: labelCreatable }: { label?: string }) => (
              <Grid p="8px" cols={['min-content', 'auto']} gutter="xxs" alignItems="center">
                <StyledCreatableLabel>Add:</StyledCreatableLabel>
                <StyledCreatableValue>{`"${labelCreatable}"`}</StyledCreatableValue>
              </Grid>
            )}
            label={option.label}
            onClick={handleOnCreateClick}
          />
        );
      }
      return null;
    }
    return null;
  }, [optionsPerSection, focusOptionIdx, selectedValues, allOptions, onCreate, inputValue, readOnly]);
};
